import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import { useQuery } from '@tanstack/react-query';
import AnnouncementCategoryAttributeFields from './AnnouncementCategoryAttributeFields';
import apiService from '../services/api';
import type { AdminAnnouncementDetail, AdminAnnouncementUpdatePayload } from '../types/announcementAdmin';
import type { Category } from '../types/category';
import type { LocationProvider } from '../types/provider';
import {
  applyCategoryAttributeCascade,
  mergeAnnouncementObjectAttributesForSubmit,
  parseAnnouncementObjectAttributes,
} from '../utils/announcementObjectAttributesForm';

type Props = {
  open: boolean;
  announcement: AdminAnnouncementDetail;
  saving?: boolean;
  onClose: () => void;
  onSave: (payload: AdminAnnouncementUpdatePayload) => void;
};

type FormState = {
  title: string;
  description: string;
  type: 'lost' | 'found';
  status: 'active' | 'resolved' | 'closed';
  category_id: number | '';
  lost_found_date: string;
  reward: string;
  contact_info: string;
  specific_location: string;
  is_public: boolean;
  is_public_place: boolean;
  location_provider_id: number | '';
  address: string;
  city: string;
  postal_code: string;
  region: string;
  country: string;
  latitude: string;
  longitude: string;
  images_json: string;
};

type AttributeFormState = {
  categoryAttributes: Record<string, string>;
  secretClues: string;
  publicTransportJson: string;
  possibleLossLocationsJson: string;
  extraAttributesJson: string;
};

function toFormState(announcement: AdminAnnouncementDetail): FormState {
  return {
    title: announcement.title ?? '',
    description: announcement.description ?? '',
    type: announcement.type === 'found' ? 'found' : 'lost',
    status:
      announcement.status === 'resolved' || announcement.status === 'closed'
        ? announcement.status
        : 'active',
    category_id: announcement.category?.id ?? '',
    lost_found_date: announcement.lost_found_date ?? '',
    reward: announcement.reward ?? '',
    contact_info: announcement.contact_info ?? '',
    specific_location: announcement.specific_location ?? '',
    is_public: announcement.is_public,
    is_public_place: announcement.is_public_place,
    location_provider_id: announcement.location_provider?.id ?? '',
    address: announcement.location?.address ?? '',
    city: announcement.location?.city ?? '',
    postal_code: announcement.location?.postal_code ?? '',
    region: announcement.location?.region ?? '',
    country: announcement.location?.country ?? 'France',
    latitude:
      announcement.location?.latitude != null ? String(announcement.location.latitude) : '',
    longitude:
      announcement.location?.longitude != null ? String(announcement.location.longitude) : '',
    images_json: JSON.stringify(announcement.images ?? [], null, 2),
  };
}

function toAttributeFormState(announcement: AdminAnnouncementDetail): AttributeFormState {
  const parsed = parseAnnouncementObjectAttributes(announcement.object_attributes);
  return {
    categoryAttributes: parsed.categoryAttributes,
    secretClues: parsed.secretClues,
    publicTransportJson: parsed.publicTransportJson,
    possibleLossLocationsJson: parsed.possibleLossLocationsJson,
    extraAttributesJson: parsed.extraAttributesJson,
  };
}

function parseJsonField<T>(value: string, fieldLabel: string): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    throw new Error(`Le champ ${fieldLabel} doit contenir du JSON valide.`);
  }
}

export default function AnnouncementAdminEditDialog({
  open,
  announcement,
  saving = false,
  onClose,
  onSave,
}: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(announcement));
  const [attributeForm, setAttributeForm] = useState<AttributeFormState>(() =>
    toAttributeFormState(announcement)
  );
  const [error, setError] = useState<string | null>(null);

  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    enabled: open,
  });

  const { data: providers = [] } = useQuery({
    queryKey: ['admin-announcement-edit-providers'],
    queryFn: async () => {
      const response = await apiService.getProviders({ limit: 1000 });
      return response.providers ?? [];
    },
    enabled: open,
  });

  const categories: Category[] = useMemo(() => categoriesResponse?.data?.categories ?? [], [categoriesResponse]);

  const selectedCategory = useMemo(
    () =>
      typeof form.category_id === 'number'
        ? categories.find((category) => category.id === form.category_id) ?? null
        : null,
    [categories, form.category_id]
  );

  useEffect(() => {
    if (open) {
      setForm(toFormState(announcement));
      setAttributeForm(toAttributeFormState(announcement));
      setError(null);
    }
  }, [announcement, open]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCategoryChange = (nextCategoryId: number | '') => {
    const previousCategoryId = form.category_id;
    updateField('category_id', nextCategoryId);
    if (previousCategoryId !== nextCategoryId) {
      setAttributeForm((prev) => ({
        ...prev,
        categoryAttributes: {},
      }));
    }
  };

  const handleAttributeChange = (key: string, value: string) => {
    setAttributeForm((prev) => ({
      ...prev,
      categoryAttributes: applyCategoryAttributeCascade(key, value, prev.categoryAttributes),
    }));
  };

  const handleSubmit = () => {
    try {
      const payload: AdminAnnouncementUpdatePayload = {
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        status: form.status,
        category_id: typeof form.category_id === 'number' ? form.category_id : undefined,
        lost_found_date: form.lost_found_date.trim() || null,
        reward: form.reward.trim() || null,
        contact_info: form.contact_info.trim() || null,
        specific_location: form.specific_location.trim() || null,
        is_public: form.is_public,
        is_public_place: form.is_public_place,
        location_provider_id:
          typeof form.location_provider_id === 'number' ? form.location_provider_id : null,
        object_attributes: mergeAnnouncementObjectAttributesForSubmit(attributeForm),
        images: parseJsonField<string[]>(form.images_json, 'images'),
        location: {
          name: form.address.trim() || form.city.trim() || 'Lieu',
          address: form.address.trim(),
          city: form.city.trim(),
          region: form.region.trim(),
          country: form.country.trim() || 'France',
          postal_code: form.postal_code.trim(),
          latitude: Number.parseFloat(form.latitude) || 0,
          longitude: Number.parseFloat(form.longitude) || 0,
        },
      };
      setError(null);
      onSave(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Formulaire invalide.');
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Modifier l&apos;annonce #{announcement.id}</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Titre"
              fullWidth
              required
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              inputProps={{ maxLength: 70 }}
              helperText={`${form.title.length}/70`}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              required
              multiline
              minRows={3}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.type}
                onChange={(e) => updateField('type', e.target.value as 'lost' | 'found')}
              >
                <MenuItem value="lost">Perdu</MenuItem>
                <MenuItem value="found">Trouvé</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                value={form.status}
                onChange={(e) =>
                  updateField('status', e.target.value as 'active' | 'resolved' | 'closed')
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Résolue</MenuItem>
                <MenuItem value="closed">Fermée</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select
                label="Catégorie"
                value={form.category_id}
                onChange={(e) =>
                  handleCategoryChange(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Date perte / trouvaille"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.lost_found_date}
              onChange={(e) => updateField('lost_found_date', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Récompense"
              fullWidth
              value={form.reward}
              onChange={(e) => updateField('reward', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact"
              fullWidth
              value={form.contact_info}
              onChange={(e) => updateField('contact_info', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lieu précis"
              fullWidth
              value={form.specific_location}
              onChange={(e) => updateField('specific_location', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Partenaire lié</InputLabel>
              <Select
                label="Partenaire lié"
                value={form.location_provider_id}
                onChange={(e) =>
                  updateField(
                    'location_provider_id',
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Aucun</MenuItem>
                {providers.map((provider: LocationProvider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_public}
                    onChange={(e) => updateField('is_public', e.target.checked)}
                  />
                }
                label="Annonce publique"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_public_place}
                    onChange={(e) => updateField('is_public_place', e.target.checked)}
                  />
                }
                label="Lieu public"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ fontWeight: 700, mb: 1 }}>Localisation</Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Adresse"
              fullWidth
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Ville"
              fullWidth
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Code postal"
              fullWidth
              value={form.postal_code}
              onChange={(e) => updateField('postal_code', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Région"
              fullWidth
              value={form.region}
              onChange={(e) => updateField('region', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Pays"
              fullWidth
              value={form.country}
              onChange={(e) => updateField('country', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Latitude"
              fullWidth
              value={form.latitude}
              onChange={(e) => updateField('latitude', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Longitude"
              fullWidth
              value={form.longitude}
              onChange={(e) => updateField('longitude', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <AnnouncementCategoryAttributeFields
              category={selectedCategory}
              objectAttributes={attributeForm.categoryAttributes}
              secretClues={attributeForm.secretClues}
              publicTransportJson={attributeForm.publicTransportJson}
              possibleLossLocationsJson={attributeForm.possibleLossLocationsJson}
              extraAttributesJson={attributeForm.extraAttributesJson}
              onAttributeChange={handleAttributeChange}
              onSecretCluesChange={(value) =>
                setAttributeForm((prev) => ({ ...prev, secretClues: value }))
              }
              onPublicTransportJsonChange={(value) =>
                setAttributeForm((prev) => ({ ...prev, publicTransportJson: value }))
              }
              onPossibleLossLocationsJsonChange={(value) =>
                setAttributeForm((prev) => ({ ...prev, possibleLossLocationsJson: value }))
              }
              onExtraAttributesJsonChange={(value) =>
                setAttributeForm((prev) => ({ ...prev, extraAttributesJson: value }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Images (JSON)"
              fullWidth
              multiline
              minRows={4}
              value={form.images_json}
              onChange={(e) => updateField('images_json', e.target.value)}
              helperText="Tableau JSON des chemins ou URLs d'images."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
